import { PlusOutlined } from "@ant-design/icons";
import { Modal, Button, Input, Form, Card, Select, Switch, Grid, theme } from "antd";
import { useState } from "react";

const { TextArea } = Input;

import QuestionGeneratorForm from "./QuestionGeneratorForm";
import { showNotification, showSuccessNotification } from "../../../utils/notification";
import SurveyQuestionCardRender from "../../common-utils/SurveyQuestionCardRender";

const SCOPE_SELECT_WIDTH = "min(240px, 100%)";

const FormBuilderModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  creationFormPrerequisites = {},
  handleSurveyCreation = () => {}
}) => {
  const {
    questionTypes = [],
    allowedScopes = [],
    coursesList = [],
    batchesList = []
  } = creationFormPrerequisites;

  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const [surveyBasicDetails, setSurveyBasicDetails] = useState({});
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [surveyScope, setSurveyScope] = useState();
  const [isCourseLevelFilterRequired, setIsCourseLevelFilterRequired] = useState(false);
  const [isBatchLevelFilterRequired, setIsBatchLevelFilterRequired] = useState(false);
  const [targetCourseId, setTargetCourseId] = useState();
  const [targetBatchId, setTargetBatchId] = useState();

  const [addNewQuestion, setAddNewQuestion] = useState(false);

  const handleSurveyBasicDetailsSubmit = (values) => {
    setSurveyBasicDetails(values);
    showSuccessNotification("Details added successfully");
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = () => {
    if (!surveyBasicDetails?.title) {
      showNotification("Missing Param", "Survey Title is required.", "error");
      return;
    }
    if (!surveyBasicDetails?.description) {
      showNotification("Missing Param", "Survey Description is required.", "error");
      return;
    }
    if ((surveyQuestions || []).length < 1) {
      showNotification("Missing Param", "At-least 1 question is required.", "error");
      return;
    }
    if (!surveyScope) {
      showNotification("Missing Param", "Survey Scope is required.", "error");
      return;
    }
    if (isBatchLevelFilterRequired && isCourseLevelFilterRequired) {
      showNotification("Bad Params", "Both Course and Batch filter cannot co-exist.", "error");
      return;
    }
    if (isCourseLevelFilterRequired && !targetCourseId) {
      showNotification("Missing Param", "Course is not selected.", "error");
      return;
    }
    if (isBatchLevelFilterRequired && !targetBatchId) {
      showNotification("Missing Param", "Batch is not selected.", "error");
      return;
    }
    handleSurveyCreation({
      title: surveyBasicDetails?.title,
      description: surveyBasicDetails?.description,
      surveyQuestions,
      surveyScope,
      targetCourseId,
      targetBatchId
    });
  };

  return (
    <Modal
      title="Create Survey"
      open={isModalOpen}
      onCancel={handleCancel}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleFormSubmit}>
          Submit
        </Button>
      ]}
    >
      <Card
        style={{
          borderTop: `4px solid ${token.colorPrimary}`,
          borderRadius: "10px",
          marginBottom: "10px"
        }}
      >
        <Form
          form={form}
          name="new_survey_basic_details"
          layout={isMobileView ? "vertical" : "horizontal"}
          onFinish={handleSurveyBasicDetailsSubmit}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[
              {
                required: true,
                message: "Please enter the survey title!"
              }
            ]}
          >
            <Input placeholder="Enter survey title" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              {
                required: true,
                message: "Please enter the survey description!"
              }
            ]}
          >
            <TextArea placeholder="Enter survey description" autoSize={{ minRows: 1, maxRows: 6 }} />
          </Form.Item>
          <Form.Item className="submit-btn">
            <Button type="primary" htmlType="submit">
              Add Info
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* QUESTIONS RENDERED */}

      <h2>Questions: </h2>
      <p> *These are just prototypes, filling any data here will not have any effect.</p>

      <SurveyQuestionCardRender
        surveyQuestions={surveyQuestions}
        setSurveyQuestions={setSurveyQuestions}
        showSuccessNotification={showSuccessNotification}
      />

      <Button type="dashed" onClick={() => setAddNewQuestion(true)} block icon={<PlusOutlined />}>
        Add Question
      </Button>
      <br />
      {addNewQuestion && (
        <QuestionGeneratorForm
          surveyQuestions={surveyQuestions}
          setSurveyQuestions={setSurveyQuestions}
          setAddNewQuestion={setAddNewQuestion}
          questionTypes={questionTypes}
        />
      )}
      <br />

      <h2>Survey Scope: </h2>
      <p>Select survey{"'"}s main Scope: </p>
      <Select
        placeholder="Select Scope"
        onChange={(value) => setSurveyScope(value)}
        options={allowedScopes}
        style={{ width: SCOPE_SELECT_WIDTH }}
        popupMatchSelectWidth={false}
      />
      <br />
      <p>Apply course level filter? </p>
      <Switch
        onChange={(checked) => {
          setIsCourseLevelFilterRequired(checked);
          if (!checked) {
            setTargetCourseId();
          }
        }}
      />
      {isCourseLevelFilterRequired && (
        <Select
          placeholder="Select Course"
          onChange={(value) => setTargetCourseId(value)}
          options={coursesList}
          style={{ width: SCOPE_SELECT_WIDTH }}
          popupMatchSelectWidth={false}
        />
      )}
      <p>Apply batch level filter? </p>
      <Switch
        onChange={(checked) => {
          setIsBatchLevelFilterRequired(checked);
          if (!checked) {
            setTargetBatchId();
          }
        }}
      />
      {isBatchLevelFilterRequired && (
        <Select
          placeholder="Select Batch"
          onChange={(value) => setTargetBatchId(value)}
          options={batchesList}
          style={{ width: SCOPE_SELECT_WIDTH }}
          popupMatchSelectWidth={false}
        />
      )}
    </Modal>
  );
};

export default FormBuilderModal;
